/**
 * Copyright (C) 2026 Checkmk GmbH - License: Checkmk Enterprise License
 * This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
 * conditions defined in the file COPYING, which is part of this source code package.
 */
import type { components, paths } from 'cmk-ai-control-plane/typescript/openapi'

import type { FastApiEvent } from '@/lib/fastapi-client/client'

import type { ControlPlaneClient } from '@/nonfree/ai/control-plane/api/transport'

type Schemas = components['schemas']

export type ConversationSummary = Schemas['ConversationSummary']
export type ConversationResponse = Schemas['ConversationResponse']
export type ContentBlock = FastApiEvent<paths, '/conversations/{conversation_id}/events'>['data']

export interface ConversationEvent {
  cursor: number
  block: ContentBlock
}

export class ConversationsApi {
  public constructor(private readonly client: ControlPlaneClient) {}

  public async create(
    idempotencyKey: string,
    signal: AbortSignal
  ): Promise<Schemas['ConversationCreatedResponse']> {
    return this.client.POST('/conversations', {
      params: { header: { 'Idempotency-Key': idempotencyKey } },
      signal
    })
  }

  public async list(): Promise<ConversationSummary[]> {
    return (await this.client.GET('/conversations')).conversations
  }

  public async get(conversationId: string): Promise<ConversationResponse> {
    return this.client.GET('/conversations/{conversation_id}', {
      params: { path: { conversation_id: conversationId } }
    })
  }

  // Stub until the control plane serves DELETE /conversations/{conversation_id}:
  // then call this.client.DELETE with the regenerated OpenAPI types.
  public async delete(_conversationId: string): Promise<void> {
    return Promise.reject(new Error('Deleting chats is not available yet.'))
  }

  public async postTurn(
    conversationId: string,
    prompt: string,
    idempotencyKey: string,
    signal: AbortSignal
  ): Promise<Schemas['TurnResponse']> {
    return this.client.POST('/conversations/{conversation_id}/turns', {
      params: {
        path: { conversation_id: conversationId },
        header: { 'Idempotency-Key': idempotencyKey }
      },
      body: { prompt },
      signal
    })
  }

  /** The events after the one whose cursor is `after`; 0 reads the conversation from its start. */
  public async *events(
    conversationId: string,
    after: number,
    signal: AbortSignal
  ): AsyncIterable<ConversationEvent> {
    const events = this.client.events('/conversations/{conversation_id}/events', {
      params: { path: { conversation_id: conversationId }, header: { 'Last-Event-ID': after } },
      signal
    })
    for await (const { id, data } of events) {
      yield { cursor: Number(id), block: data }
    }
  }
}
